/**
 * This is helper class will be utilized for Omnichannel configuration
 */
import _isEmpty from 'lodash/isEmpty';
import Page from './page';
import i18n from './reacti18n';

// Get Remote config for Ominichannel config from window
export const getOmnichannelConfig = () => {
  try {
    if (window && window.omniChannelConfig) {
      return window.omniChannelConfig;
    }
    return {};
  } catch (e) {
    return {};
  }
};

export const returnRefIDforIntentCode = (intentCode) => {
  const omniConfig = getOmnichannelConfig().setContext.supportedIntents;
  return Object.keys(omniConfig).find((key) => omniConfig[key] === intentCode) || '';
};

export const returnRefIdEnabledPrompts = (refId) => {
  if (refId) {
    return getOmnichannelConfig().promtEnabledIntents[refId] || '';
  }
  return '';
};

// this funciton is used to validate whether setContext wired-on /off ?
export const isSetContextFeatureWiredOn = (config) => {
  if (!_isEmpty(config) && !_isEmpty(config.setContext)) {
    return config.setContext.isFeatureWiredON || false;
  }
  return false;
};

// this funciton is used to validate whether  isSetContextEnabledGlobally ?
export const isSetContextEnabledGlobally = (config) => {
  if (!_isEmpty(config) && !_isEmpty(config.setContext)) {
    return config.setContext.isEnabledGlobally || false;
  }
  return false;
};

// Check if it setContext is enabled
export const isMarketEnabledForSetContext = (config) => {
  const country = Page.getCountry().toUpperCase();
  const locale = Page.getAPILocalilty();
  if (!_isEmpty(config) && !_isEmpty(config.setContext)) {
    const enabledMarkets = config.setContext.enabledMarkets;
    if (enabledMarkets) {
      return (
        enabledMarkets
          .split(',')
          .filter((market) => market.indexOf(`${country}:`) >= 0)
          .filter((selectedMarket) => selectedMarket.split(':')[1].indexOf(locale) >= 0).length > 0
      );
    }
  }
  return false;
};

export const isSetContextEnabledFromRC = () => {
  const config = getOmnichannelConfig();
  if (
    isSetContextFeatureWiredOn(config) &&
    (isSetContextEnabledGlobally(config) || isMarketEnabledForSetContext(config))
  )
    return true;
  // default return false;
  return false;
};

/**
 * Function to parse intent information from SAC
 */
const getSacIntents = (topicDetails) => {
  const intentCode = topicDetails.intentCode;
  const customerTreatment = topicDetails.customerIntent;
  const internalTreatment = topicDetails.internalTreatment;

  const intentResp = [{}];

  if (intentCode) {
    intentResp[0].code = intentCode;
  }

  if (customerTreatment) {
    intentResp[0].customer_intent = customerTreatment;
  }

  if (internalTreatment) {
    intentResp[0].internal_treatment = internalTreatment;
  }

  return intentResp;
};

export const getStandardIntent = (topicRefId) => {
  const config = getOmnichannelConfig();
  if (!_isEmpty(config) && !_isEmpty(config.setContext) && !_isEmpty(config.setContext.supportedIntents)) {
    const standardIntents = config.setContext.supportedIntents;
    if (standardIntents[topicRefId]) {
      return [{ code: standardIntents[topicRefId] }];
    }
  }
  return [];
};

/**
 * Function to check for topic refid in remote config for contact us page
 */
export const isFinalIntentEnabledForSetContext = (topicDetails = {}, responseBody) => {
  const sacStandardIntents = getSacIntents(topicDetails);

  // checking for intents availability in SAC
  if (Array.isArray(sacStandardIntents) && sacStandardIntents.length > 0 && !_isEmpty(sacStandardIntents[0])) {
    return sacStandardIntents;
  }

  // checking for remote config existance and topic refid existance in the remote config
  const topicRefId = topicDetails.topicRefId;
  const arrStandardIntents = getStandardIntent(topicRefId);
  if (arrStandardIntents.length > 0) return arrStandardIntents;

  // checking for tags info in article
  if (
    responseBody.articleContent &&
    Array.isArray(responseBody.articleContent.tags) &&
    responseBody.articleContent.tags.length &&
    Array.isArray(responseBody.articleContent.tags[0].predictions) &&
    responseBody.articleContent.tags[0].predictions.length
  ) {
    const articleTags = responseBody.articleContent.tags[0].predictions;
    // Parsing article repsonse by changing intent to customer_intent to pass to set context API
    return articleTags.map(({ intent, score }) => ({ customer_intent: intent, score }));
  }

  // returning empty array for exception cases
  return [];
};

/**
 * Function to check whether user is loggedIn and intents exist for making set context API Call
 */
export const isSetContextEnabled = (topicDetails, articleResponse) => {
  const isIntentsEnabled = isFinalIntentEnabledForSetContext(topicDetails, articleResponse);

  if (
    Page.isLoggedIn() && // Logged-in customer
    isSetContextEnabledFromRC() && // enabled in RC
    Array.isArray(isIntentsEnabled) && // Has intents available
    isIntentsEnabled.length > 0
  ) {
    return isIntentsEnabled;
  }
  return [];
};

/**
 * Function to get context Id from Set context response to pass it to update context after step up
 */
export const getContextId = (setContextIntents, setContextResponse) => {
  let intentResp;
  return setContextIntents.map((intent) => {
    const intentKeys = Object.keys(intent);
    setContextResponse.filter((intents) =>
      intentKeys.map((intentKey) => {
        if (intents.intent[intentKey] === intent[intentKey]) {
          intentResp = intents;
          return true;
        }
        return false;
      }),
    );
    return intentResp;
  });
};

/**
 * Function to check is key exists in locale property file.
 * Pass key to this function to check whether key exists in property file or not
 * @returns {boolean} true/false
 */
export const isKeyExistInPropertyFile = (filepath, keyValue) => {
  const snowManCode = '%E2%98%83';
  const keyLabel = i18n.message(`${filepath}.${keyValue}`);
  return !encodeURI(keyLabel).includes(snowManCode);
};

/**
 * Function to check intent code exists in the omni-channel articles
 * Used for both desktop and minihelp/Venice
 */
export const isIntentCodeExists = (omniChannelIntentArticles) =>
  !!(
    !_isEmpty(omniChannelIntentArticles) &&
    Array.isArray(omniChannelIntentArticles.intentArticleDetails) &&
    omniChannelIntentArticles.intentArticleDetails.length > 0 &&
    omniChannelIntentArticles.intentArticleDetails[0].channelDetails &&
    omniChannelIntentArticles.intentArticleDetails[0].channelDetails.intentCode
  );

/**
 * Function to Send out the same intent_code and customer_code as setContext
 * If the array contains more than one customer_intent then send out the highest intent
 */

export const getAsyncChatPillOCPParams = (chatPillOcpParams) => {
  const ocpParams = {
    customerIntent: null,
    intentCode: null,
  };
  if (Array.isArray(chatPillOcpParams) && chatPillOcpParams.length > 0) {
    if (chatPillOcpParams.length > 1) {
      const maxScoreValue = chatPillOcpParams.reduce((previousScore, currentScore) =>
        previousScore.score > currentScore.score ? previousScore : currentScore,
      );
      ocpParams.customerIntent = maxScoreValue.customer_intent;
    } else {
      if (chatPillOcpParams[0].customer_intent) {
        ocpParams.customerIntent = chatPillOcpParams[0].customer_intent;
      }
      if (chatPillOcpParams[0].intent_code) {
        ocpParams.intentCode = chatPillOcpParams[0].intent_code;
      }
    }
  }
  return ocpParams;
};

/**
 * Function to get isCustomerjourneyAPIEnabled RC flag from omnichannel config
 */
export const isCustomerJourneyAPIEnabled = () => getOmnichannelConfig().isCustomerJourneyEnabled || false;
